<template>
  <div class="act-marker" aria-hidden="true">
    <div class="pip pip-1">
      <span class="pip-dot"></span>
      <span>Draw</span>
    </div>
    <span class="sep"></span>
    <div class="pip pip-2">
      <span class="pip-dot"></span>
      <span>Send</span>
    </div>
    <span class="sep"></span>
    <div class="pip pip-3">
      <span class="pip-dot"></span>
      <span>Receive</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "ActMarker",
};
</script>

<style lang="scss" scoped>
/* Three-pip rhythm marker: DRAW · SEND · RECEIVE.
   Animation runs on a 6s loop, matching ScrawlDemo's master tempo so the
   marker reads as in-sync when both are on screen together — but it loops
   independently and works fine when ActMarker stands alone (auth pages). */

.act-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: $font-body;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: $ink-faint;
  height: 18px;

  .pip {
    display: flex;
    align-items: center;
    gap: 6px;
    transition: color $dur-base $ease-soft;
  }
  .pip-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba($muted-lavender, 0.4);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
    transition: background $dur-base $ease-soft, box-shadow $dur-base $ease-soft;
  }
  /* Mock's .sep is a soft horizontal gradient hairline, not a typographic ·.
     Reads as an in-progress timeline rather than a list separator. */
  .sep {
    width: 14px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba($muted-lavender, 0.4), transparent);
    border: 0;
    opacity: 1;
  }

  .pip-1 .pip-dot { animation: am-pip1 6s $ease-soft infinite; }
  .pip-2 .pip-dot { animation: am-pip2 6s $ease-soft infinite; }
  .pip-3 .pip-dot { animation: am-pip3 6s $ease-soft infinite; }
  .pip-1 { animation: am-pipText1 6s $ease-soft infinite; }
  .pip-2 { animation: am-pipText2 6s $ease-soft infinite; }
  .pip-3 { animation: am-pipText3 6s $ease-soft infinite; }
}

/* Active pip dot uses the holographic conic + a chrome-blue inset
   highlight so the dot reads as a tiny iridescent bubble (matching mock
   line 414: `.actmarker .pip.on .dot { background: var(--holo);
   box-shadow: 0 0 12px var(--bubblegum), inset 0 1px 0 #fff; }`).
   Off-state mirrors the soft lilac-tinted dot from the mock. */
@keyframes am-pip1 {
  0%, 35%   { background: $holo; box-shadow: 0 0 12px rgba($bubblegum, 0.55), inset 0 1px 0 #fff; }
  37%, 100% { background: rgba($muted-lavender, 0.4); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6); }
}
@keyframes am-pip2 {
  0%, 35%   { background: rgba($muted-lavender, 0.4); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6); }
  37%, 51%  { background: $holo; box-shadow: 0 0 12px rgba($bubblegum, 0.55), inset 0 1px 0 #fff; }
  53%, 100% { background: rgba($muted-lavender, 0.4); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6); }
}
@keyframes am-pip3 {
  0%, 51%   { background: rgba($muted-lavender, 0.4); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6); }
  53%, 85%  { background: $holo; box-shadow: 0 0 12px rgba($bubblegum, 0.55), inset 0 1px 0 #fff; }
  87%, 100% { background: rgba($muted-lavender, 0.4); box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6); }
}
@keyframes am-pipText1 {
  0%, 35%   { color: $bubblegum-deep; }
  37%, 100% { color: $ink-faint; }
}
@keyframes am-pipText2 {
  0%, 35%   { color: $ink-faint; }
  37%, 51%  { color: $bubblegum-deep; }
  53%, 100% { color: $ink-faint; }
}
@keyframes am-pipText3 {
  0%, 51%   { color: $ink-faint; }
  53%, 85%  { color: $bubblegum-deep; }
  87%, 100% { color: $ink-faint; }
}

@media (prefers-reduced-motion: reduce) {
  .act-marker .pip-1, .act-marker .pip-2, .act-marker .pip-3,
  .act-marker .pip-1 .pip-dot, .act-marker .pip-2 .pip-dot, .act-marker .pip-3 .pip-dot {
    animation: none !important;
  }
  .act-marker .pip-3 { color: $bubblegum-deep; }
  .act-marker .pip-3 .pip-dot {
    background: $holo;
    box-shadow:
      0 0 12px rgba($bubblegum, 0.55),
      inset 0 1px 0 #fff;
  }
}
</style>
